import React, { useState, useEffect } from 'react';

import {
  Box,
  Text,
  Input,
  VStack,
  HStack,
  Switch,
  Button,
  InputGroup,
  InputRightElement,
  Select,
  Flex,
} from '@chakra-ui/react';

import { useColors } from './colors';

const QuantityAdjustments = ({ storeData, onStoreUpdate }) => {
  const [maxQuantityEnabled, setMaxQuantityEnabled] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [quantityOffset, setQuantityOffset] = useState('');
  const [maxQuantity, setMaxQuantity] = useState('');
  const [priceRanges, setPriceRanges] = useState([
    { minPrice: 1, maxPrice: 200, quantity: 10 },
  ]);

  const suppliers = storeData?.suppliers || [];
  const {
    borderColor,
    cardBgColor,
    inputColor,
    placeholderColor,
    textColor,
    hoverBgColor,
    inputBgColor,
  } = useColors();

  useEffect(() => {
    if (suppliers.length > 0 && !selectedSupplier) {
      setSelectedSupplier(suppliers[0].supplierId);
    }
  }, [suppliers, selectedSupplier]);

  useEffect(() => {
    if (selectedSupplier && suppliers.length > 0) {
      const supplier = suppliers.find((s) => s.supplierId === selectedSupplier);
      if (supplier) {
        setQuantityOffset(supplier.quantityOffset || '');
        setMaxQuantityEnabled(supplier.defaultQuantityMaxLimitEnabled || false);
        setMaxQuantity(supplier.quantityMaxLimit || '');
        setPriceRanges(
          supplier.priceRangeQuantities?.length > 0 ? supplier.priceRangeQuantities : [],
        );
      }
    }
  }, [selectedSupplier, suppliers]);

  const updateSupplierData = (field, value) => {
    if (!selectedSupplier || !storeData) return;

    const updatedSuppliers = storeData.suppliers.map((supplier) =>
      supplier.supplierId === selectedSupplier
        ? { ...supplier, [field]: value }
        : supplier,
    );

    const updatedStore = {
      ...storeData,
      suppliers: updatedSuppliers,
    };

    onStoreUpdate(updatedStore);
  };

  const addPriceRange = () => {
    const newPriceRanges = [...priceRanges, { minPrice: '', maxPrice: '', quantity: '' }];
    setPriceRanges(newPriceRanges);
    updateSupplierData('priceRangeQuantities', newPriceRanges);
  };

  const removePriceRange = (index) => {
    const newPriceRanges = priceRanges.filter((_, i) => i !== index);
    setPriceRanges(newPriceRanges);
    updateSupplierData('priceRangeQuantities', newPriceRanges);
  };

  const handleSupplierChange = (e) => {
    setSelectedSupplier(e.target.value);
  };

  const handleMaxQuantityToggle = (e) => {
    const isEnabled = e.target.checked;
    setMaxQuantityEnabled(isEnabled);
    updateSupplierData('defaultQuantityMaxLimitEnabled', isEnabled);
  };

  const handleQuantityOffsetChange = (e) => {
    const value = Number(e.target.value);
    setQuantityOffset(value);
    updateSupplierData('quantityOffset', value);
  };

  const handleMaxQuantityChange = (e) => {
    const value = Number(e.target.value);
    setMaxQuantity(value);
    updateSupplierData('quantityMaxLimit', value);
  };

  const handlePriceRangeChange = (index, field, value) => {
    const newRanges = [...priceRanges];
    newRanges[index] = { ...newRanges[index], [field]: Number(value) || value };
    setPriceRanges(newRanges);
    updateSupplierData('priceRangeQuantities', newRanges);
  };

  return (
    <Box mt={6}>
      <Text fontSize="xl" fontWeight="bold" mb={6} color={textColor}>
        Quantity Adjustments
      </Text>

      <Flex gap={6}>
        <Box width="30%">
          <Text mb={2} fontWeight="medium" color={textColor}>
            Supplier
          </Text>
          <Select
            value={selectedSupplier}
            onChange={handleSupplierChange}
            placeholder="Select supplier"
            size="md"
            borderColor="gray.300"
            color={inputColor}
            bg={inputBgColor}
            sx={{
              option: {
                bg: cardBgColor,
                color: inputColor,
              },
              '> option:hover': {
                bg: hoverBgColor + '!important',
              },
              '> option:checked': {
                bg: 'blue.50' + '!important',
              },
            }}
            _hover={{
              bg: hoverBgColor,
            }}
            _focus={{
              bg: inputBgColor,
              borderColor: 'blue.300',
            }}
          >
            {suppliers.map((supplier) => (
              <option key={supplier.supplierId} value={supplier.supplierId}>
                {supplier.altName}
              </option>
            ))}
          </Select>
        </Box>

        {/* Adjustments Card - 70% width */}
        <Box width="70%" bg={cardBgColor} borderRadius="xl" p={6}>
          <VStack spacing={8} align="stretch">
            {/* Quantity Offset */}
            <Box>
              <Text mb={2} fontWeight="medium" color={textColor}>
                Quantity Offset
              </Text>
              <InputGroup size="md" maxW="40%">
                <Input
                  type="number"
                  placeholder="Enter offset"
                  value={quantityOffset}
                  onChange={handleQuantityOffsetChange}
                  borderColor="gray.300"
                  _hover={{ borderColor: 'gray.400' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                  color={inputColor}
                  bg={inputBgColor}
                  _placeholder={{ color: placeholderColor }}
                />
                <InputRightElement width="2.5rem" pointerEvents="none">
                  <Text color="gray.500">#</Text>
                </InputRightElement>
              </InputGroup>
            </Box>

            {/* Default Max Quantity */}
            <Box width="100%">
              <HStack mb={2} justify="space-between" maxW="40%">
                <Text fontWeight="medium" color={textColor}>
                  Default Max Quantity Limit
                </Text>
                <Switch
                  colorScheme="blue"
                  isChecked={maxQuantityEnabled}
                  onChange={handleMaxQuantityToggle}
                />
              </HStack>
              {maxQuantityEnabled && (
                <InputGroup size="md" maxW="40%">
                  <Input
                    type="number"
                    placeholder="Enter max quantity"
                    value={maxQuantity}
                    onChange={handleMaxQuantityChange}
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                    color={inputColor}
                    bg={inputBgColor}
                    _placeholder={{ color: placeholderColor }}
                  />
                  <InputRightElement width="2.5rem" pointerEvents="none">
                    <Text color="gray.500">#</Text>
                  </InputRightElement>
                </InputGroup>
              )}
            </Box>

            {/* Price Range Based Quantities */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Text fontSize="lg" fontWeight="medium" color={textColor}>
                  Price Range Based Quantities
                </Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={addPriceRange}
                >
                  Add Range
                </Button>
              </HStack>

              <VStack spacing={4} align="stretch">
                {priceRanges.map((range, index) => (
                  <HStack key={index} spacing={4} align="center">
                    <Text color={textColor} minW="20px" textAlign="center">
                      {index + 1}.
                    </Text>
                    <InputGroup size="sm" maxW="120px">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={range.minPrice || ''}
                        onChange={(e) =>
                          handlePriceRangeChange(index, 'minPrice', e.target.value)
                        }
                        borderColor="gray.300"
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                        color={inputColor}
                        bg={inputBgColor}
                        _placeholder={{ color: placeholderColor }}
                      />
                      <InputRightElement width="2rem" pointerEvents="none">
                        <Text color="gray.500" fontSize="xs">
                          $
                        </Text>
                      </InputRightElement>
                    </InputGroup>

                    <Text color={textColor}>to</Text>

                    <InputGroup size="sm" maxW="120px">
                      <Input
                        type="number"
                        placeholder="Max"
                        value={range.maxPrice || ''}
                        onChange={(e) =>
                          handlePriceRangeChange(index, 'maxPrice', e.target.value)
                        }
                        borderColor="gray.300"
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                        color={inputColor}
                        bg={inputBgColor}
                        _placeholder={{ color: placeholderColor }}
                      />
                      <InputRightElement width="2rem" pointerEvents="none">
                        <Text color="gray.500" fontSize="xs">
                          $
                        </Text>
                      </InputRightElement>
                    </InputGroup>

                    <Text color={textColor}>=</Text>

                    <InputGroup size="sm" maxW="100px">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={range.allowedQuantity || ''}
                        onChange={(e) =>
                          handlePriceRangeChange(index, 'allowedQuantity', e.target.value)
                        }
                        borderColor="gray.300"
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                        color={inputColor}
                        bg={inputBgColor}
                        _placeholder={{ color: placeholderColor }}
                      />
                      <InputRightElement width="2rem" pointerEvents="none">
                        <Text color="gray.500" fontSize="xs">
                          #
                        </Text>
                      </InputRightElement>
                    </InputGroup>

                    <Button
                      size="sm"
                      fontSize="2xl"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => removePriceRange(index)}
                      isDisabled={priceRanges.length === 1}
                    >
                      ×
                    </Button>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default QuantityAdjustments;
